import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import type { AIUseCase } from "../../types/ai-integration";
import "./AIIntegrationSection.css";

interface AIIntegrationSectionProps {
  isLive: boolean;
  onNavigateToSettings: () => void;
  useCases: AIUseCase[];
  compatibleTools: string[];
}

const useReducedMotion = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
};

const UseCaseCard: React.FC<{
  useCase: AIUseCase;
  index: number;
  reducedMotion: boolean;
}> = ({ useCase, index, reducedMotion }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const { t } = useTranslation("marketing");

  const animationProps = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 30 } as const,
        animate: inView
          ? ({ opacity: 1, y: 0 } as const)
          : ({ opacity: 0, y: 30 } as const),
        transition: { duration: 0.5, delay: index * 0.1 },
      };

  return (
    <motion.div
      ref={ref}
      className="ai-integration__card"
      style={
        {
          "--ai-card-accent": useCase.accentColor,
        } as React.CSSProperties
      }
      {...animationProps}
    >
      <div className="ai-integration__card-icon" aria-hidden="true">
        {useCase.icon}
      </div>
      <h3 className="ai-integration__card-title">{t(useCase.titleKey)}</h3>
      <p className="ai-integration__card-description">
        {t(useCase.descriptionKey)}
      </p>
    </motion.div>
  );
};

const AIIntegrationSection: React.FC<AIIntegrationSectionProps> = ({
  isLive,
  onNavigateToSettings,
  useCases,
  compatibleTools,
}) => {
  const { t } = useTranslation("marketing");
  const reducedMotion = useReducedMotion();
  const headerRef = useRef(null);
  const headerInView = useInView(headerRef, { once: true });

  const headerAnimationProps = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 30 } as const,
        animate: headerInView
          ? ({ opacity: 1, y: 0 } as const)
          : ({ opacity: 0, y: 30 } as const),
        transition: { duration: 0.5 },
      };

  const footerRef = useRef(null);
  const footerInView = useInView(footerRef, { once: true });

  const footerAnimationProps = reducedMotion
    ? {}
    : {
        initial: { opacity: 0, y: 20 } as const,
        animate: footerInView
          ? ({ opacity: 1, y: 0 } as const)
          : ({ opacity: 0, y: 20 } as const),
        transition: { duration: 0.5, delay: 0.2 },
      };

  return (
    <section
      id="ai-integration"
      className="ai-integration"
      aria-label={t("landing.aiIntegration.title")}
    >
      <div className="ai-integration__content">
        <motion.div
          ref={headerRef}
          className="ai-integration__header"
          {...headerAnimationProps}
        >
          <span className="ai-integration__badge">
            {t("landing.aiIntegration.badge")}
          </span>
          <h2 className="ai-integration__title">
            {t("landing.aiIntegration.title")}
          </h2>
          <p className="ai-integration__subtitle">
            {t("landing.aiIntegration.subtitle")}
          </p>
          <p className="ai-integration__mcp-explanation">
            {t("landing.aiIntegration.mcpExplanation")}
          </p>
        </motion.div>

        <div className="ai-integration__grid">
          {useCases.map((useCase, index) => (
            <UseCaseCard
              key={useCase.titleKey}
              useCase={useCase}
              index={index}
              reducedMotion={reducedMotion}
            />
          ))}
        </div>

        <motion.div
          ref={footerRef}
          className="ai-integration__footer"
          {...footerAnimationProps}
        >
          <div className="ai-integration__tools">
            <span className="ai-integration__tools-label">
              {t("landing.aiIntegration.compatibleTools")}
            </span>
            <div className="ai-integration__tools-badges">
              {compatibleTools.map((tool) => (
                <span key={tool} className="ai-integration__tool-badge">
                  {tool}
                </span>
              ))}
            </div>
          </div>

          {isLive ? (
            <button
              className="ai-integration__cta"
              onClick={onNavigateToSettings}
              aria-label={t("landing.aiIntegration.ctaAriaLabel")}
            >
              {t("landing.aiIntegration.cta")}
            </button>
          ) : (
            <button
              className="ai-integration__cta ai-integration__cta--disabled"
              disabled
              aria-label={t("landing.aiIntegration.ctaComingSoon")}
            >
              {t("landing.aiIntegration.ctaComingSoon")}
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default AIIntegrationSection;
